import { PointAddResponse } from "@1games-ru/region-shared/dist/client/requests/point";
import {
  AddressStreetType,
  AddressType,
} from "@1games-ru/region-shared/dist/enums/address/address";
import { PointType } from "@1games-ru/region-shared/dist/enums/point/point";
import { Position } from "@1games-ru/region-shared/dist/types/world/world";
import * as Tooltip from "@radix-ui/react-tooltip";
import { AnimatePresence, motion } from "motion/react";
import { useEffect, useId, useMemo, useState } from "react";
import { Spinner } from "@radix-ui/themes";
import AdminMpProvider from "@/core/data-provider/data-mp/admin/admin-mp-provider";
import { cn } from "@/core/helpers/cn";
import { ArrowLeftIcon, BucketIcon, CheckIcon, MapPin } from "@/shared/icons";
import { Button, Icon, Select, SelectOption, TextInput } from "@/shared/ui";
import useAddressPointSet from "@/domains/createEntity/hooks/useAddressPointSet";
import useAddressStreetSet from "@/domains/createEntity/hooks/useAddressStreetSet";
import useAddressStreets, { AddressStreet } from "@/domains/createEntity/hooks/useAddressStreets";
import usePoint from "@/domains/createEntity/hooks/usePoint";
import usePointsByAddressId from "@/domains/createEntity/hooks/usePointsByAddressId";
import CreateEntityScroll from "@/domains/createEntity/components/CreateEntityScroll";
import CreateEntityTitle from "@/domains/createEntity/components/CreateEntityTitle";

type Props = {
  onClose?: () => void;
};

function SimpleTooltip({
  children,
  content,
}: {
  children: React.ReactNode;
  content: React.ReactNode;
}) {
  return (
    <Tooltip.Provider>
      <Tooltip.Root>
        <Tooltip.Trigger asChild>{children}</Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content
            className="w-fit bg-dark-900 p-1 px-2.5 border border-dark-700 rounded text-sm shadow-lg animate-fade-in"
            sideOffset={5}
            side="top"
            align="center"
          >
            {content}
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    </Tooltip.Provider>
  );
}

function StreetItem({
  street,
  showType,
  onSaveSuccess,
  onViewPoints,
}: {
  street: AddressStreet;
  showType: boolean;
  onSaveSuccess: (name: string) => Promise<void>;
  onViewPoints: (streetId: number) => void;
}) {
  const { mutateAsync } = useAddressStreetSet();
  const [isPending, setIsPending] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState(street.name);

  async function handleSave() {
    setIsPending(true);
    const trimmed = name.trim();
    if (!trimmed || trimmed === street.name) {
      setIsEditing(false);
      setIsPending(false);
      setName(street.name);
      return;
    }

    const ok = await mutateAsync({
      streetId: street.id,
      streetName: trimmed,
      streetType: street.type,
    });

    if (ok !== false) {
      await onSaveSuccess(trimmed);
      setIsEditing(false);
    }

    setIsPending(false);
  }

  return (
    <div className="p-4 pl-2 bg-dark-800 rounded-lg">
      <div className="flex items-start gap-3">
        <div className="flex-1">
          {isEditing ? (
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Название улицы"
              className="w-full bg-dark-900 rounded-md p-2"
              disabled={isPending}
            />
          ) : (
            <div className="text-white p-2">{street.name}</div>
          )}
          {showType && <div className="text-white/70 text-sm px-2">{street.type}</div>}
        </div>

        <div className="flex gap-2">
          <SimpleTooltip content="Переименовать">
            <button
              type="button"
              className="shrink-0 p-2 rounded-md hover:bg-white/5 transition-colors"
              onClick={() => (isEditing ? handleSave() : setIsEditing(true))}
              disabled={isPending}
            >
              {isEditing ? (
                <Icon name="checked" className="w-4 bg-[#84FF97]" />
              ) : (
                <Icon name="documentPencil" className="w-4" />
              )}
            </button>
          </SimpleTooltip>

          <SimpleTooltip content="Посмотреть точки">
            <button
              type="button"
              className="shrink-0 p-2 rounded-md hover:bg-white/5 transition-colors"
              onClick={() => onViewPoints(street.id)}
            >
              <MapPin className="size-4" />
            </button>
          </SimpleTooltip>
        </div>
      </div>
    </div>
  );
}

function StreetPointItem({
  streetId,
  streetName,
  index,
  position,
  onSuccess,
}: {
  streetId: number;
  streetName: string;
  index: number;
  position: Position;
  onSuccess: () => Promise<void>;
}) {
  const { addPoint, clearPoints } = usePoint();
  const { mutateAsync: saveAddressPoint } = useAddressPointSet();
  const [isEditing, setIsEditing] = useState(false);
  const [addressPoint, setAddressPoint] = useState<PointAddResponse | null>(null);
  const stablePointId = useId();

  useEffect(() => {
    if (!isEditing) {
      setAddressPoint(null);
      return;
    }

    (async () => {
      const p = await addPoint({
        groupName: "address",
        id: `street-${streetId}-edit-${stablePointId}`,
        type: PointType.Place,
        content: `${streetName} #${index}`,
        position,
        dimension: null,
        heading: null,
        range: 1,
      });
      setAddressPoint(p);
    })();
  }, [isEditing, addPoint, streetId, stablePointId, streetName, index, position]);

  return (
    <div
      className={cn(
        "flex flex-col gap-2 p-4 border border-transparent",
        isEditing && "border-gray-800 rounded-lg",
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <div>
          {streetName} #{index}
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="surface"
            className="h-8 px-3 text-xs"
            onClick={async () => {
              await AdminMpProvider.teleportByCoords(position);
            }}
          >
            Телепорт
          </Button>

          <button
            type="button"
            className="shrink-0 p-2 text-white rounded-md hover:bg-white/5 transition-colors"
            onClick={() => {
              setIsEditing((v) => {
                if (v) {
                  setAddressPoint(null);
                  clearPoints();
                }
                return !v;
              });
            }}
          >
            <Icon name="documentPencil" className="w-4" />
          </button>
        </div>
      </div>

      {isEditing && (
        <div className="mt-2 p-4 bg-dark-800 rounded-lg space-y-3">
          <div className="text-white/80 text-sm">{`${streetName} #${index}`}</div>

          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <Button
                variant="surface"
                className="h-8 px-3 text-xs"
                onClick={async () => {
                  const p = await addPoint({
                    groupName: "address",
                    id: `street-${streetId}-edit-${stablePointId}`,
                    type: PointType.Place,
                    content: `${streetName} #${index}`,
                    position: null,
                    dimension: null,
                    heading: null,
                    range: 1,
                  });
                  setAddressPoint(p);
                }}
              >
                <div className="flex items-center gap-2">
                  <MapPin className="size-4" />
                  <span>Установить</span>
                </div>
              </Button>

              <Button
                variant="surface"
                className="h-8 px-3 text-xs disabled:pointer-events-none"
                disabled={addressPoint === null}
                onClick={async () => {
                  setAddressPoint(null);
                  await clearPoints();
                }}
              >
                <div className="flex items-center gap-2">
                  <BucketIcon className="size-4" />
                </div>
              </Button>
            </div>

            <Button
              variant="solid"
              className="h-8 px-3 text-xs disabled:pointer-events-none disabled:opacity-50"
              disabled={!addressPoint}
              onClick={async () => {
                const ok = await saveAddressPoint({
                  streetId,
                  pointType: AddressType.House,
                  index,
                  position: addressPoint?.position || null,
                });
                if (ok !== false) {
                  setIsEditing(false);
                  setAddressPoint(null);
                  await onSuccess();
                }
              }}
            >
              <CheckIcon className="size-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function Filter({
  search,
  setSearch,
  selectedType,
  setSelectedType,
}: {
  search: string;
  setSearch: (search: string) => void;
  selectedType: AddressStreetType | "All";
  setSelectedType: (type: AddressStreetType | "All") => void;
}) {
  const streetTypeOptions = useMemo<(AddressStreetType | "All")[]>(
    () => ["All", ...Object.values(AddressStreetType)],
    [],
  );

  return (
    <div className="flex gap-3">
      <TextInput
        value={search}
        onChange={(text) => setSearch(text)}
        placeholder="Поиск"
        className="flex-1"
      />

      <Select
        placeholder="Тип"
        className="z-20 min-w-[10rem] h-full"
        bg="bg-dark-800"
        value={
          <div className="text-white/90">{selectedType === "All" ? "Все типы" : selectedType}</div>
        }
      >
        {streetTypeOptions.map((typeOption) => (
          <SelectOption
            key={typeOption}
            onClick={() => setSelectedType(typeOption)}
            isSelected={selectedType === typeOption}
          >
            {typeOption === "All" ? "Все типы" : typeOption}
          </SelectOption>
        ))}
      </Select>
    </div>
  );
}

function AddStreetForm({
  streetTypeOptions,
  onSuccess,
}: {
  streetTypeOptions: AddressStreetType[];
  onSuccess: () => Promise<void>;
}) {
  const { mutateAsync } = useAddressStreetSet();
  const [newStreetName, setNewStreetName] = useState("");
  const [newStreetType, setNewStreetType] = useState<AddressStreetType>(AddressStreetType.Street);

  async function handleAddStreet() {
    const ok = await mutateAsync({
      streetId: null,
      streetName: newStreetName,
      streetType: newStreetType,
    });

    if (ok !== false) {
      setNewStreetName("");
      setNewStreetType(AddressStreetType.Street);
      await onSuccess();
    }
  }

  return (
    <>
      <div className="text-lg font-medium px-2">Добавить улицу</div>
      <div className="flex flex-col gap-3">
        <TextInput
          placeholder="Название улицы"
          className="flex-1"
          value={newStreetName}
          onChange={(text) => setNewStreetName(text)}
        />
        <Select
          placeholder="Тип"
          className="z-10"
          bg="bg-dark-800"
          value={<div className="text-white/90">{newStreetType}</div>}
        >
          {streetTypeOptions.map((typeOption) => (
            <SelectOption
              key={typeOption}
              onClick={() => setNewStreetType(typeOption)}
              isSelected={newStreetType === typeOption}
            >
              {typeOption}
            </SelectOption>
          ))}
        </Select>
        <Button onClick={() => handleAddStreet()}>Добавить улицу</Button>
      </div>
    </>
  );
}

function AddStreetPointForm({
  selectedStreetId,
  maxIndex,
  data,
  onSuccess,
}: {
  selectedStreetId: number;
  maxIndex: number;
  data: AddressStreet[];
  onSuccess: () => Promise<void>;
}) {
  const { addPoint, clearPoints } = usePoint();
  const stablePointId = useId();
  const [addressPoint, setAddressPoint] = useState<PointAddResponse | null>(null);
  const { mutateAsync: saveAddressPoint } = useAddressPointSet();
  const nextIndex = maxIndex + 1;

  return (
    <div className="mt-6 p-4 bg-dark-800 rounded-lg space-y-3">
      <div className="text-white/80 text-sm">
        {(() => {
          const streetName = data?.find((s) => s.id === selectedStreetId)?.name || "";
          return `${streetName} #${nextIndex}`;
        })()}
      </div>

      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button
            variant="surface"
            className="h-8 px-3 text-xs"
            onClick={async () => {
              const streetName = data?.find((s) => s.id === selectedStreetId)?.name || "";
              const p = await addPoint({
                groupName: "address",
                id: `street-${selectedStreetId}-new-${stablePointId}`,
                type: PointType.Place,
                content: `${streetName} #${nextIndex}`,
                position: null,
                dimension: null,
                heading: null,
                range: 1,
              });
              setAddressPoint(p);
            }}
          >
            <div className="flex items-center gap-2">
              <MapPin className="size-4" />
              <span>Установить</span>
            </div>
          </Button>

          <Button
            variant="surface"
            className="h-8 px-3 text-xs disabled:pointer-events-none"
            disabled={addressPoint === null}
            onClick={async () => {
              setAddressPoint(null);
              await clearPoints();
            }}
          >
            <div className="flex items-center gap-2">
              <BucketIcon className="size-4" />
            </div>
          </Button>
        </div>

        <Button
          variant="solid"
          className="h-8 px-3 text-xs disabled:pointer-events-none disabled:opacity-50"
          disabled={!addressPoint || selectedStreetId === null}
          onClick={async () => {
            if (selectedStreetId === null) return;
            const ok = await saveAddressPoint({
              streetId: selectedStreetId,
              pointType: AddressType.House,
              index: nextIndex,
              position: addressPoint?.position || null,
            });
            if (ok !== false) {
              setAddressPoint(null);
              await onSuccess();
            }
          }}
        >
          <CheckIcon className="size-4" />
        </Button>
      </div>
    </div>
  );
}

export default function CreateStreetAddressFormContainer({ onClose }: Props) {
  const { data, isPending, refetch } = useAddressStreets();
  const [search, setSearch] = useState("");
  const [selectedType, setSelectedType] = useState<AddressStreetType | "All">("All");
  const streetTypeOptions = useMemo<AddressStreetType[]>(
    () => Object.values(AddressStreetType),
    [],
  );
  const [selectedStreetId, setSelectedStreetId] = useState<number | null>(null);
  const {
    data: points,
    isPending: isPointsPending,
    refetch: refetchPoints,
    reset,
  } = usePointsByAddressId(selectedStreetId);
  const { clearPoints } = usePoint();

  const filtered = useMemo(
    () =>
      (data || []).filter((street) => {
        const matchesType = selectedType === "All" || street.type === selectedType;
        const matchesSearch = street.name.toLowerCase().includes(search.toLowerCase());
        return matchesType && matchesSearch;
      }),
    [data, search, selectedType],
  );

  useEffect(() => {
    return () => {
      clearPoints();
    };
  }, [clearPoints]);

  return (
    <>
      <CreateEntityTitle title="Улицы и адреса" onClose={onClose} />

      <Filter
        search={search}
        setSearch={setSearch}
        selectedType={selectedType}
        setSelectedType={setSelectedType}
      />

      <AddStreetForm streetTypeOptions={streetTypeOptions} onSuccess={() => refetch()} />
      <div className="h-4" />

      <CreateEntityScroll>
        {selectedStreetId === null ? (
          <div className="h-full overflow-y-auto space-y-3">
            {filtered.map((street) => (
              <StreetItem
                key={street.id}
                street={street}
                showType={selectedType === "All"}
                onSaveSuccess={async () => {
                  await refetch();
                }}
                onViewPoints={(streetId) => setSelectedStreetId(streetId)}
              />
            ))}

            {data !== null && filtered.length === 0 && !isPending && (
              <div className="text-white/70 text-sm text-center py-8">Ничего не найдено</div>
            )}
          </div>
        ) : (
          <div className="my-4">
            <Button
              variant="solid-surface"
              className="h-8 px-3 text-xs flex items-center gap-2"
              onClick={async () => {
                setSelectedStreetId(null);
                reset();
                await clearPoints();
              }}
            >
              <ArrowLeftIcon className="size-3" />
              Назад
            </Button>

            {/* Create new address point */}
            {!isPointsPending && selectedStreetId !== null && points !== null && (
              <AddStreetPointForm
                selectedStreetId={selectedStreetId}
                maxIndex={Math.max(0, ...(points?.map((p) => p.index) ?? []))}
                data={data ?? []}
                onSuccess={async () => {
                  await Promise.all([refetch(), refetchPoints(), clearPoints()]);
                }}
              />
            )}

            {isPointsPending && (
              <div className="flex items-center justify-center py-8">
                <Spinner className="size-6" />
              </div>
            )}

            {!isPointsPending && points !== null && points.length > 0 && (
              <div className="text-white/70 text-sm text-center py-4 space-y-3">
                <AnimatePresence>
                  {points.map((point) => (
                    <motion.div
                      key={point.id}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 4 }}
                      transition={{ duration: 0.18, ease: "easeOut" }}
                      style={{ willChange: "transform, opacity" }}
                    >
                      <StreetPointItem
                        streetId={point.streetId}
                        streetName={point.streetName}
                        index={point.index}
                        position={point.position}
                        onSuccess={async () => {
                          await Promise.all([refetchPoints(), clearPoints()]);
                        }}
                      />
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            )}

            {!isPointsPending && points !== null && points.length === 0 && (
              <div className="text-white/70 text-sm text-center py-8">
                <div>На этой улице пока нет адресов</div>
              </div>
            )}
          </div>
        )}
      </CreateEntityScroll>
    </>
  );
}
